import { defineStore } from "pinia";
import {
  AppNotification,
  NotificacoesService,
} from "@/services/notification/notificacoes.service";
import {
  NotificacaoNovaPayload,
  NotificationType,
} from "@/services/notification/notificacoes.types";
import { useAuthStoreUser } from "@/stores/app/authUser";
import { useUiStore } from "./uiStore";
import { useChatStore } from "./chatStore";
import { IdAtendimento } from "../../services/common-types";
import { toast } from 'vue3-toastify';
import { showBrowserNotification } from "@/services/notification/browser-notification.service";

const notificacoesService = new NotificacoesService();

export const useNotificationStore = defineStore("notificationStore", {
  state: () => ({
    notifications: [] as AppNotification[],
    isLoading: false,
  }),

  getters: {
    unreadCount: (state) => state.notifications.filter((n) => !n.lida).length,

    notificationsWithTimeAgo: (state) => {
      const now = new Date();
      return state.notifications.map((n) => {
        const diff = Math.floor(
          (now.getTime() - new Date(n.criadaEm).getTime()) / 1000,
        );
        let timeAgo: string;
        if (diff < 60) timeAgo = "agora";
        else if (diff < 3600) timeAgo = `${Math.floor(diff / 60)}m`;
        else if (diff < 86400) timeAgo = `${Math.floor(diff / 3600)}h`;
        else timeAgo = `${Math.floor(diff / 86400)}d`;
        return { ...n, timeAgo };
      });
    },

    unreadNotifications: (state) => state.notifications.filter((n) => !n.lida),

    readNotificationsLast15Days: (state) => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 15);
      return state.notifications.filter(
        (n) => n.lida && new Date(n.criadaEm) >= cutoff,
      );
    },
  },

  actions: {
    // ─── Inicialização ────────────────────────────────────────────────────

    async fetchNotifications() {
      const authStore = useAuthStoreUser();
      const idConta = authStore.currentConta?.id;

      if (!idConta) return;

      this.isLoading = true;
      try {
        this.notifications = await notificacoesService.listar(String(idConta));
      } catch (error) {
        console.error(
          "[NotificationStore] Erro ao buscar notificações:",
          error,
        );
      } finally {
        this.isLoading = false;
      }
    },

    // Chamado pelo store de socket após o connect (igual ao chatInternoStore)
    handleNovaNotificacao(payload: NotificacaoNovaPayload) {
      console.log("[NotificationStore] handleNovaNotificacao chamado", payload);
      const jaExiste = this.notifications.some((n) => n.id === payload.id);
      if (jaExiste) return;

      this.notifications = [payload, ...this.notifications];

      // Pop-up nativo do navegador (preferência do usuário + permissão do navegador).
      // A checagem de permissão fica dentro de showBrowserNotification.
      const authStore = useAuthStoreUser();
      if (authStore.user?.notificacaoNavegador) {
        showBrowserNotification(payload, () => {
          void this.markAsRead(payload.id);
          this.handleClickNotificacao(payload);
        });
      }
    },

    // ─── Ações do usuário ─────────────────────────────────────────────────

    async markAsRead(id: string) {
      const notif = this.notifications.find((n) => n.id === id);
      if (!notif || notif.lida) return;

      // Otimista: atualiza localmente antes da requisição
      notif.lida = true;

      try {
        const authStore = useAuthStoreUser();
        const idConta = authStore.currentConta?.id;
        if (!idConta) return;

        await notificacoesService.marcarComoLida(String(idConta), id);
      } catch (error) {
        // Rollback em caso de falha
        notif.lida = false;
        console.error("[NotificationStore] Erro ao marcar como lida:", error);
      }
    },

    async markAllAsRead() {
      const naoLidas = this.notifications.filter((n) => !n.lida);
      if (!naoLidas.length) return;

      // Otimista
      naoLidas.forEach((n) => (n.lida = true));

      try {
        const authStore = useAuthStoreUser();
        const idConta = authStore.currentConta?.id;
        if (!idConta) return;

        await notificacoesService.marcarTodasComoLidas(String(idConta));
      } catch (error) {
        // Rollback
        naoLidas.forEach((n) => (n.lida = false));
        console.error(
          "[NotificationStore] Erro ao marcar todas como lidas:",
          error,
        );
      }
    },
    handleClickNotificacao(notif: AppNotification) {
      const { tipo, referenceId } = notif;
      const uiStore = useUiStore();
      const chatStore = useChatStore();

      switch (tipo) {
        case NotificationType.ATENDIMENTO_TRANSFERIDO:
          if (notif.atendimentoFinalizado) {
            toast.error("Este atendimento já foi finalizado");
            return;
          }

          if (referenceId) void chatStore.focusAtendimento(referenceId as IdAtendimento);
          break;

        case NotificationType.ATENDIMENTO_FINALIZADO:
          uiStore.openReportsModal("general");
          break;

        case NotificationType.SINCRONIZACAO_CONTATOS_CONCLUIDA:
          uiStore.openContactsOperationsHistoryModal("whatsapp");
          break;

        case NotificationType.IMPORTACAO_CONTATOS_CONCLUIDA:
          uiStore.openContactsOperationsHistoryModal("planilha");
          break;

        case NotificationType.EXPORTACAO_CONVERSAS_CONCLUIDA:
        case NotificationType.EXPORTACAO_CONVERSAS_ERRO:
          uiStore.openExportacoesModal(referenceId);
          break;
      }
    },
  },
});
